import { Loader2 } from 'lucide-react'
import { getSignatureStatusLabel } from '../../../docusign/signatureStatusLabel'

type SelfEnrollmentDocusignModalProps = {
  isOpen: boolean
  isSignatureSigned: boolean
  isFinalizingDocusign: boolean
  isCheckingSignature: boolean
  isDocusignIframeLoading: boolean
  signatureStatus: string | null
  docusignUrl: string | null
  docusignFrameKey: number
  onIframeLoad: () => void
  onClose: () => void
  onContinue: () => void
}

export function SelfEnrollmentDocusignModal({
  isOpen,
  isSignatureSigned,
  isFinalizingDocusign,
  isCheckingSignature,
  isDocusignIframeLoading,
  signatureStatus,
  docusignUrl,
  docusignFrameKey,
  onIframeLoad,
  onClose,
  onContinue
}: SelfEnrollmentDocusignModalProps) {
  if (!isOpen) {
    return null
  }

  return (
    <div
      className="fixed inset-0 !z-[100000] flex items-center justify-center bg-black/70 p-0 sm:px-3 sm:py-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="docusign-modal-title"
    >
      <div className="flex h-[100svh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-[94vh] sm:max-h-[940px] sm:max-w-6xl sm:rounded-2xl xl:max-w-7xl">
        <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 id="docusign-modal-title" className="text-lg font-semibold text-gray-900">
              Sign Your Enrollment Agreement
            </h2>
            <p className="mt-1 text-xs leading-5 text-gray-500 sm:text-sm">
              Complete the agreement below. We will continue automatically after DocuSign confirms
              your signature.
            </p>
          </div>
          <div className="w-fit shrink-0 rounded-full bg-orange-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary sm:text-xs">
            {isSignatureSigned
              ? 'Signed'
              : isFinalizingDocusign
                ? 'Finalizing signature'
                : isCheckingSignature
                  ? 'Checking status'
                  : signatureStatus
                    ? `Status: ${getSignatureStatusLabel(signatureStatus)}`
                    : 'Waiting for signature'}
          </div>
        </div>

        <div className="min-h-0 flex-1 bg-gray-50">
          {docusignUrl ? (
            <div className="relative h-full w-full bg-white">
              {(isDocusignIframeLoading || isFinalizingDocusign) && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/95 px-6 text-center text-sm text-gray-500">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {isFinalizingDocusign ? 'Finalizing your signature' : 'Loading secure signing'}
                    </p>
                    <p className="mt-1">
                      {isFinalizingDocusign
                        ? 'Please wait while we confirm completion with DocuSign.'
                        : 'Your DocuSign session is opening.'}
                    </p>
                  </div>
                </div>
              )}
              <iframe
                key={docusignFrameKey}
                src={docusignUrl}
                title="DocuSign signing ceremony"
                onLoad={onIframeLoad}
                className={`h-full w-full bg-white ${isFinalizingDocusign ? 'opacity-0' : ''}`}
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
              DocuSign agreement is being prepared.
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:px-5 sm:py-4 md:flex-row md:items-center md:justify-between">
          <p className="text-xs leading-5 text-gray-500 sm:text-sm">
            Please do not close this window until signing is complete.
          </p>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
            {!isSignatureSigned && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Back to enrollment
              </button>
            )}
            <button
              type="button"
              onClick={onContinue}
              disabled={!isSignatureSigned}
              className="rounded-full bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
